import { DateHelper, PhoneNumberHelper } from "@swivl-mono/helpers";
import { CallDirection, CallRecordingResponse, CallRecordingSortColumn, SortDirection } from "@swivl-mono/types";
import { DataTable, Tooltip, TruncatedCell } from "@swivl-mono/ui";
import { PaginationState, SortingState } from "@tanstack/react-table";

import { CallDirectionIcon } from "../../../../../Components/Icons/CallDirectionIcon";
import { formatDuration } from "../../../../../Helpers/Time/formatDuration";
import CallRecordingsModel from "../../../../../Models/CallRecordings/CallRecordings.Model";

type RecordingRow = CallRecordingResponse & { id: string };

function mapColumnIdToCallRecordingSortColumn(id: string): CallRecordingSortColumn | null {
  switch (id) {
    case "createdAt":
      return CallRecordingSortColumn.CREATED_AT;
    case "callDuration":
      return CallRecordingSortColumn.CALL_DURATION;
    case "userName":
      return CallRecordingSortColumn.USER_NAME;
    case "callOutcome":
      return CallRecordingSortColumn.CALL_OUTCOME;
    case "agentName":
      return CallRecordingSortColumn.AGENT_NAME;
    case "userPhoneNumber":
      return CallRecordingSortColumn.USER_PHONE_NUMBER;
    case "locationName":
      return CallRecordingSortColumn.LOCATION_NAME;
    case "callDirection":
      return CallRecordingSortColumn.CALL_DIRECTION;
    default:
      return null;
  }
}

const buildColumns = () => [
  { header: "Customer", accessorKey: "userName" },
  {
    header: "Call Duration",
    accessorKey: "callDuration",
    cell: ({ row }: { row: { original: RecordingRow; getValue: (key: string) => number } }) => {
      const duration = row.getValue("callDuration");
      const direction = row.original.callDirection;

      const directionLabel = direction === CallDirection.INBOUND ? "Inbound" : "Outbound";
      const formatted = formatDuration(duration || 0);

      return (
        <Tooltip content={directionLabel} classNames={{ wrapper: "w-fit" }}>
          <div className="flex items-center gap-2">
            <CallDirectionIcon direction={direction} />
            <span className="body-sm">{formatted}</span>
          </div>
        </Tooltip>
      );
    }
  },
  { header: "Call Outcome", accessorKey: "callOutcome" },
  {
    header: "Agent",
    accessorKey: "agentName",
    cell: ({ row }: { row: { getValue: (key: string) => string | null } }) => {
      const agentName = row.getValue("agentName");
      return <TruncatedCell text={agentName || ""} />;
    }
  },
  {
    header: "Date & Time",
    accessorKey: "createdAt",
    cell: ({ row }: { row: { original: RecordingRow } }) => {
      return <span className="text-sm text-gray-900">{DateHelper.formatDateTime(row.original.createdAt)}</span>;
    }
  },
  {
    header: "Phone",
    accessorKey: "userPhoneNumber",
    cell: ({ row }: { row: { original: RecordingRow; getValue: (key: string) => string | null } }) => {
      const rawPhone = row.getValue("userPhoneNumber");
      const formattedPhone = rawPhone ? PhoneNumberHelper.formatPhoneNumber(rawPhone) : undefined;

      return <span className="text-sm text-gray-900">{formattedPhone || ""}</span>;
    }
  },
  { header: "Location", accessorKey: "locationName" }
];

const RecordingsDataTable = ({
  data,
  handleSelectCell,
  onSelectedIdsChange,
  selectedRowId
}: {
  data: CallRecordingResponse[];
  handleSelectCell: (id: string) => void;
  onSelectedIdsChange: (ids: string[]) => void;
  selectedRowId?: string | null;
}) => {
  const { page, totalPages, limit, total, isLoading } = CallRecordingsModel.useState((state) => ({
    page: state.page,
    totalPages: state.totalPages,
    limit: state.limit,
    total: state.total,
    isLoading: state.isLoading
  }));

  const rows: RecordingRow[] = data.map((r) => ({ ...r, id: r.callSid }));
  const columns = buildColumns();

  // Convert 1-based page to 0-based pageIndex for react-table
  const pageIndex = page - 1;
  const pageSize = limit;

  const handlePaginationChange = (newPagination: PaginationState) => {
    const newPage = newPagination.pageIndex + 1; // Convert back to 1-based

    if (newPage !== page) {
      void CallRecordingsModel.setPage(newPage);
    }

    if (newPagination.pageSize !== pageSize) {
      void CallRecordingsModel.setLimit(newPagination.pageSize);
    }
  };

  const handleSortingChange = (sorting: SortingState) => {
    // Only support columns that backend can sort by
    const sort = sorting
      .map((item) => {
        const column = mapColumnIdToCallRecordingSortColumn(item.id);

        if (!column) {
          return null;
        }

        const direction: SortDirection = item.desc ? SortDirection.DESC : SortDirection.ASC;
        return `${column}:${direction}`;
      })
      .filter((v): v is string => v !== null);

    void CallRecordingsModel.setSorting(sort);
  };

  return (
    <div className="h-full w-full bg-white">
      <DataTable
        columns={columns}
        data={rows}
        areRowsSelectable={true}
        handleSelectCell={handleSelectCell}
        hasCheckboxColumn={true}
        onSelectedIdsChange={onSelectedIdsChange}
        selectedRowId={selectedRowId}
        loading={isLoading}
        manualConfig={{
          manualPagination: true,
          manualSorting: true,
          manualFiltering: false
        }}
        onSortingChange={handleSortingChange}
        onPaginationChange={handlePaginationChange}
        pageCount={totalPages}
        pagination={{ pageIndex, pageSize }}
        totalCount={total}
      />
    </div>
  );
};

export default RecordingsDataTable;
